import React from "react";
import { observer } from "mobx-react-lite";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import { registerView } from "@frui.ts/views";
import type { ViewComponent } from "@frui.ts/views";
import { Check, CollectionCheck } from "@frui.ts/bootstrap";
import Button from "@edaz/edazkit/Button/index";
import { formatDateTime, formatNumber } from "utils/localizations";
import DeadLetterTopicsListViewModel from "viewModels/administration/deadLetterTopics/search/deadLetterTopicsSearchListViewModel";
import type DeadLetterTopicMessageResponse from "entities/deadLetterTopicMessageResponse";
import ListView from "views/common/list";
import DeadLetterTopicsSearchFilters from "./deadLetterTopicsSearchFilters";

const DeadLetterTopicsSearchListView: ViewComponent<DeadLetterTopicsListViewModel> = observer(({ vm }) => {
  const columns: ColumnDefinition<DeadLetterTopicMessageResponse>[] = [
    ...(vm.allowWrite
      ? [
          {
            title: <Check custom target={vm as { allItems?: boolean }} property="allItems" threeState={true} />,
            valueFormatter: (props: { item: DeadLetterTopicMessageResponse }) => {
              if (props.item.isSelectable) {
                return <CollectionCheck custom target={vm} property="selectedItems" disabled={false} value={props.item} />;
              }
            },
          },
        ]
      : []),
    {
      property: "originalProducedAt",
      title: vm.translateAttribute("originalProducedAt"),
      valueFormatter: props => formatDateTime(props.item.originalProducedAt),
      cellClassName: vm.allowWrite ? "font-weight-bold text-right" : "font-weight-bold text-left",
      headerClassName: vm.allowWrite ? "text-right" : "text-left",
    },
    {
      property: "messageType",
      title: vm.translate("messageType"),
      valueFormatter: props => vm.translateTypeState(props.item.messageType),
    },
    {
      property: "topic",
      title: vm.translateAttribute("topic"),
      valueFormatter: props => vm.translateTopicState(props.item.topic),
    },
    {
      property: "deliveryAttempts",
      title: vm.translateAttribute("deliveryAttempts"),
      valueFormatter: props => formatNumber(props.item.deliveryAttempts),
    },
  ];

  const actionButtons = (item: DeadLetterTopicMessageResponse) => {
    const onDetailClick = vm.onItemClick();

    return (
      <>
        <Button variant="success" size="sm" className="rounded" onClick={() => (onDetailClick ? onDetailClick(item) : "")}>
          {vm.translate("showDetail")}
        </Button>
      </>
    );
  };

  return (
    <>
      <DeadLetterTopicsSearchFilters vm={vm} />
      <ListView vm={vm} hideTitle={true} columns={columns} showDetailWithList={true} actionButtons={actionButtons} />
    </>
  );
});

registerView(DeadLetterTopicsSearchListView, DeadLetterTopicsListViewModel);
